import {
  EVENT_DETAILS_NETWORK_FACEBOOK,
  EVENT_DETAILS_NETWORK_INSTAGRAM,
  EVENT_DETAILS_NETWORK_LINKEDIN,
  EVENT_DETAILS_NETWORK_TIKTOK,
  EVENT_DETAILS_NETWORK_YOUTUBE,
} from "../i18n/keysTranslations";
import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN,
  STATES,
  TIKTOK,
  YOUTUBE,
} from "./constants";

const ORGANIC_PLATFORM_LABEL_KEYS = {
  [INSTAGRAM]: EVENT_DETAILS_NETWORK_INSTAGRAM,
  [FACEBOOK]: EVENT_DETAILS_NETWORK_FACEBOOK,
  [TIKTOK]: EVENT_DETAILS_NETWORK_TIKTOK,
  [YOUTUBE]: EVENT_DETAILS_NETWORK_YOUTUBE,
  [LINKEDIN]: EVENT_DETAILS_NETWORK_LINKEDIN,
};

const normalizePlatformKey = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === "linkedin") return "linkedIn";
  return lower;
};

export const normalizeOrganicPublishPlatformFromResult = (entry) =>
  normalizePlatformKey(entry?.platform ?? entry?.platformId ?? entry?.name);

export const getOrganicPlatformLabel = (platformKey, t) => {
  if (!platformKey) return "";
  const labelKey = ORGANIC_PLATFORM_LABEL_KEYS[platformKey];
  if (labelKey) return t(labelKey);
  const normalized = String(platformKey);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const getFailedOrganicResults = (response) => {
  const results = response?.results;
  if (!Array.isArray(results)) return [];
  return results.filter((entry) => entry && entry.ok === false);
};

export const getFailedPlatformsFromOrganicResults = (response) => {
  const results = response?.results;
  if (!Array.isArray(results)) return [];

  const failed = new Set();
  for (const entry of results) {
    if (!entry || entry.ok !== false) continue;
    const key = normalizeOrganicPublishPlatformFromResult(entry);
    if (key) failed.add(key);
  }
  return [...failed];
};

export const areAllOrganicResultsOk = (response) => {
  const results = response?.results;
  if (!Array.isArray(results) || results.length === 0) return false;
  return results.every((r) => r && r.ok === true);
};

export const isOrganicPublishFullySuccessful = (response) => {
  if (!response || response.ok === false) {
    return false;
  }

  if (response.scheduled === true) {
    return true;
  }

  return areAllOrganicResultsOk(response);
};

export const readOrganicAutomatedRetryHints = (response) => ({
  isScheduledManualRetry: response?.isScheduledManualRetry,
  autoRetryQueued: response?.autoRetryQueued,
});

export const isOrganicPlatformRetryable = ({
  states,
  platformsData,
  platformKey,
}) => {
  const state = states?.[platformKey];
  const platformData = platformsData?.[platformKey];

  if (state === STATES.PUBLISHED) return false;
  if (state === STATES.FAILED) return true;
  if (platformData?.lastErrorMessage && state !== STATES.PUBLISHED) return true;

  return false;
};

export const getRetryablePlatformsFromScheduledContent = ({
  platforms = [],
  states = {},
  platformsData = {},
}) =>
  platforms.filter((platformKey) =>
    isOrganicPlatformRetryable({
      states,
      platformsData,
      platformKey,
    })
  );

export const getRetryablePlatformsFromScheduledEvent = (event) => {
  if (!event) return [];

  const originalData = event.originalData || {};
  const platforms = Array.isArray(event.platforms) ? event.platforms : [];
  const states = event.states || originalData.states || {};
  const platformsData = event.platformsData || originalData.platformsData || {};

  return getRetryablePlatformsFromScheduledContent({
    platforms,
    states,
    platformsData,
  });
};

export const buildFailureRowsFromOrganicResults = (response, t) =>
  getFailedOrganicResults(response).map((entry) => {
    const normalizedKey = normalizeOrganicPublishPlatformFromResult(entry);
    const platformLabel =
      getOrganicPlatformLabel(normalizedKey, t) ||
      entry?.platform ||
      entry?.platformId ||
      entry?.name ||
      "";
    const apiError = typeof entry?.error === "string" ? entry.error.trim() : "";

    return {
      platformLabel,
      message: apiError || "—",
    };
  });

export const buildFailureRowsFromScheduledContent = ({
  platforms = [],
  states = {},
  platformsData = {},
  t,
}) => {
  const rows = [];

  for (const platformKey of platforms) {
    if (!isOrganicPlatformRetryable({ states, platformsData, platformKey })) {
      continue;
    }

    const platformData = platformsData[platformKey];
    const lastMessageRaw = platformData?.lastErrorMessage;
    const lastMessage =
      lastMessageRaw != null && lastMessageRaw !== ""
        ? String(lastMessageRaw).trim()
        : "";

    rows.push({
      platformLabel: getOrganicPlatformLabel(platformKey, t),
      message: lastMessage || "—",
    });
  }

  return rows;
};

export const isScheduledEventRetryable = (event) =>
  getRetryablePlatformsFromScheduledEvent(event).length > 0;
